import { IconRefresh, IconArrowRight, IconReportAnalytics, IconArrowLeft, IconTrophy, IconTarget, IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import Stars from './Stars';
import WpmChart from './WpmChart';

const Kbd = ({ children, light }) => (
    <kbd className={`ml-1 hidden rounded-md border px-1.5 py-px font-sans text-[10.5px] font-bold leading-none sm:inline-block ${light ? 'border-white/40 text-white/90' : 'border-[#DDD6CF] text-[#8A837C] dark:border-zinc-600 dark:text-zinc-400'}`}>{children}</kbd>
);

const Stat = ({ label, value, sub, tone }) => (
    <div className="flex flex-col rounded-2xl border border-[#EEE9E4] bg-white px-4 py-3.5 dark:border-zinc-800 dark:bg-zinc-900">
        <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#A8A099] dark:text-zinc-500">{label}</span>
        <span className={`mt-1 text-[26px] font-bold leading-none tabular-nums ${tone || 'text-[#1C1815] dark:text-zinc-100'}`}>{value}</span>
        {sub ? <span className="mt-1 text-[11.5px] text-[#B3ABA3] dark:text-zinc-500">{sub}</span> : null}
    </div>
);

/** Accuracy as a ring: green from 95%, amber from 85%, red below. */
function AccuracyRing({ value, label, text }) {
    const r = 34;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(100, value));
    const color = pct >= 95 ? '#10b981' : pct >= 85 ? '#f59e0b' : '#ef4444';
    return (
        <div className="relative flex h-[92px] w-[92px] flex-none items-center justify-center">
            <svg viewBox="0 0 84 84" className="absolute inset-0 -rotate-90">
                <circle cx="42" cy="42" r={r} fill="none" strokeWidth="7" className="stroke-[#EFEBE7] dark:stroke-zinc-800" />
                <circle cx="42" cy="42" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
                    strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
            </svg>
            <div className="flex flex-col items-center">
                <span className="text-[19px] font-bold leading-none tabular-nums text-[#1C1815] dark:text-zinc-100">{text}</span>
                <span className="mt-0.5 text-[10px] font-semibold uppercase text-[#A8A099]">{label}</span>
            </div>
        </div>
    );
}

/**
 * Shown after any run, inside the practice window. `data` carries the scored
 * fields; `kind` is 'strict' | 'free'. The number formatter `n` localizes digits.
 * `weakKeys` = labels of the keys missed most this run (strict runs only).
 */
export default function ResultCard({ kind, data, stars, passed, chart, n, t, isBn, onRetry, onNext, nextLabel, onClose, weakKeys = [], target = null }) {
    const pct = (v) => `${n(Math.round(v))}%`;
    const wpm = Math.round(kind === 'free' ? data.netWpm : data.wpm);
    const good = passed !== false;
    const heading = kind === 'free'
        ? (passed == null ? t('typingPractice.result.title') : passed ? t('typingPractice.result.examPassTitle') : t('typingPractice.result.examFailTitle'))
        : (passed ? t('typingPractice.result.passed') : t('typingPractice.result.tryAgain'));
    const note = kind === 'free'
        ? (passed == null ? null : passed ? t('typingPractice.result.examPass') : t('typingPractice.result.examFail'))
        : null;

    return (
        <div className="flex flex-col gap-4">
            {/* hero */}
            <div className={`relative overflow-hidden rounded-3xl border p-5 sm:p-6 ${good
                ? 'border-primary/20 bg-gradient-to-br from-primary/[0.10] via-white to-amber-50/60 dark:from-primary/[0.14] dark:via-zinc-900 dark:to-zinc-900'
                : 'border-red-200/70 bg-gradient-to-br from-red-50 via-white to-white dark:border-red-900/40 dark:from-red-900/10 dark:via-zinc-900 dark:to-zinc-900'}`}
            >
                <div className="flex flex-col items-center gap-5 text-center sm:flex-row sm:text-left">
                    <div className="flex flex-col items-center sm:items-start">
                        <span className="text-[64px] font-extrabold leading-none tracking-tight tabular-nums text-primary sm:text-[76px]">{n(wpm)}</span>
                        <span className="mt-1 text-[12px] font-bold uppercase tracking-[0.1em] text-[#8A837C] dark:text-zinc-400">
                            {t('typingPractice.result.wpmLong')}
                        </span>
                    </div>
                    <AccuracyRing value={data.accuracy} text={pct(data.accuracy)} label={t('typingPractice.result.accuracy')} />
                    <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5 sm:items-end sm:text-right">
                        <span className={`flex h-10 w-10 items-center justify-center rounded-2xl ${good ? 'bg-primary text-white shadow-[0_6px_16px_rgba(230,138,0,0.35)]' : 'bg-red-500 text-white'}`}>
                            {kind === 'free' && passed != null
                                ? (passed ? <IconCircleCheck size={22} /> : <IconCircleX size={22} />)
                                : <IconTrophy size={21} />}
                        </span>
                        <span className="font-hind text-[19px] font-bold text-[#1C1815] dark:text-zinc-100">{heading}</span>
                        {stars != null && <Stars value={stars} size={22} />}
                        {note && <span className="max-w-[300px] text-[12.5px] leading-snug text-[#6B635C] dark:text-zinc-400">{note}</span>}
                        {target != null && (
                            <span className="flex items-center gap-1 text-[12px] font-semibold text-[#8A837C] dark:text-zinc-400">
                                <IconTarget size={14} /> {t('typingPractice.result.target', { n: n(target) })}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {kind === 'free' ? (
                    <>
                        <Stat label={t('typingPractice.result.grossWpm')} value={n(Math.round(data.wpm))} sub={isBn ? '৫ স্ট্রোক = ১ শব্দ' : '5 strokes = 1 word'} />
                        <Stat label={t('typingPractice.result.errorRate')} value={pct(data.errorRate)} tone={data.errorRate <= 5 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'} />
                        <Stat label={t('typingPractice.result.words')} value={n(data.correctWords)} sub={`${n(data.wrongWords)} ${t('typingPractice.result.wrong')}`} />
                        <Stat label={t('typingPractice.result.time')} value={`${n(Math.round(data.seconds))}${isBn ? ' সে.' : 's'}`} />
                    </>
                ) : (
                    <>
                        <Stat label={t('typingPractice.result.correct')} value={n(data.correct)} sub={isBn ? '৫ স্ট্রোক = ১ শব্দ' : '5 strokes = 1 word'} />
                        <Stat label={t('typingPractice.result.mistakes')} value={n(data.errors)} tone={data.errors ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'} />
                        <Stat label={t('typingPractice.result.time')} value={`${n(Math.round(data.seconds))}${isBn ? ' সে.' : 's'}`} />
                        <Stat label={t('typingPractice.result.units')} value={n(data.units)} />
                    </>
                )}
            </div>

            {chart && chart.length > 1 && (
                <div className="rounded-2xl border border-[#EEE9E4] bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
                    <div className="mb-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-[#6B635C] dark:text-zinc-400">
                        <IconReportAnalytics size={15} /> {t('typingPractice.result.speedOverTime')}
                    </div>
                    <WpmChart points={chart} isBn={isBn} />
                </div>
            )}

            {weakKeys.length > 0 && (
                <div className="rounded-2xl border border-[#EEE9E4] bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
                    <div className="mb-2 text-[12.5px] font-semibold text-[#6B635C] dark:text-zinc-400">{t('typingPractice.result.weakKeys')}</div>
                    <div className="flex flex-wrap gap-1.5">
                        {weakKeys.map((k) => (
                            <span key={k.key} className="flex items-center gap-1.5 rounded-lg border border-red-200/70 bg-red-50 px-2.5 py-1 dark:border-red-900/40 dark:bg-red-900/10">
                                <span className="font-hind text-[16px] font-semibold text-red-600 dark:text-red-400">{k.label}</span>
                                <span className="text-[11px] font-semibold text-red-400">{n(k.errors)}×</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-col-reverse gap-2.5 sm:flex-row">
                {onClose && (
                    <button type="button" onClick={onClose}
                        className="flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-[14px] font-semibold text-[#6B635C] transition-colors hover:bg-black/5 dark:text-zinc-400 dark:hover:bg-white/5">
                        <IconArrowLeft size={17} /> {t('typingPractice.result.backToMenu')} <Kbd>Esc</Kbd>
                    </button>
                )}
                <div className="flex flex-1 gap-2.5 sm:justify-end">
                    <button type="button" onClick={onRetry}
                        className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-[14px] font-semibold transition-all sm:flex-none sm:px-6 ${onNext
                            ? 'border border-[#E6E1DC] bg-white text-[#3D3833] hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200'
                            : 'bg-primary text-white shadow-[0_6px_16px_rgba(230,138,0,0.3)] hover:bg-primary/90'}`}>
                        <IconRefresh size={17} /> {t('typingPractice.result.retry')} <Kbd light={!onNext}>{onNext ? 'Tab' : 'Enter'}</Kbd>
                    </button>
                    {onNext && (
                        <button type="button" onClick={onNext}
                            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 text-[14px] font-semibold text-white shadow-[0_6px_16px_rgba(230,138,0,0.3)] transition-colors hover:bg-primary/90 sm:flex-none sm:px-6">
                            {nextLabel || t('typingPractice.result.next')} <IconArrowRight size={17} /> <Kbd light>Enter</Kbd>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
